import React from 'react';
import { NumberChoice } from '../../../components/technician/ui/NumberChoice';
import { NumberStepper } from '../../../components/technician/ui/NumberStepper';
import { SegmentedChoice } from '../../../components/technician/ui/SegmentedChoice';
import { CONTROL_KIND } from '../../../lib/rcu/catalog/control';
import { assertNever } from '../../../lib/exhaustive';
import { TARGET_VALUE_SPECS, controlForValue } from '../catalog/value';
import type { MappingTarget } from '../targets';
import { valueFitsTarget } from '../targets';

export interface TargetValueControlProps {
  readonly target: MappingTarget;
  readonly value: number;
  readonly onChange: (value: number) => void;
}

/**
 * The control that edits one output's value.
 *
 * Which control it is comes from the kind of value the output takes, not from a
 * choice made here: a relay is on or off, a dimmer is a level, a motor is a
 * direction. That keeps a technician from ever setting a relay to 40%.
 */
export const TargetValueControl: React.FC<TargetValueControlProps> = ({
  target,
  value,
  onChange,
}) => {
  const spec = TARGET_VALUE_SPECS[target.valueKind];
  const hint = valueFitsTarget(target, value)
    ? undefined
    : `${spec.format(value)} is not something ${target.label} can be set to.`;

  const control = controlForValue(target.valueKind);

  switch (control) {
    case CONTROL_KIND.TOGGLE:
      return (
        <SegmentedChoice
          label={spec.label}
          value={String(value)}
          onChange={(next) => onChange(Number(next))}
          options={spec.presets.map((preset) => ({
            value: String(preset),
            label: spec.format(preset),
          }))}
          hint={hint}
        />
      );
    case CONTROL_KIND.LEVEL:
      return (
        <div>
          <NumberStepper
            label={spec.label}
            value={value}
            min={spec.min}
            max={spec.max}
            onChange={onChange}
            hint={hint ?? levelHint(spec.floor)}
          />
          <div className="mt-2">
            <NumberChoice
              label="Presets"
              values={spec.presets}
              value={value}
              onChange={onChange}
              format={(preset) => spec.format(preset)}
            />
          </div>
        </div>
      );
    case CONTROL_KIND.POSITION:
      return (
        <SegmentedChoice
          label={spec.label}
          value={String(value)}
          onChange={(next) => onChange(Number(next))}
          options={spec.presets.map((preset) => ({
            value: String(preset),
            label: spec.format(preset),
          }))}
          columns={3}
          hint={hint}
        />
      );
    default:
      return assertNever(control);
  }
};

const levelHint = (floor: number | null): string | undefined =>
  floor === null || floor <= 0
    ? undefined
    : `The hardware acts from ${floor}% up, so a lower level is sent as ${floor}%.`;

export default TargetValueControl;
